import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { CookieBanner } from "@/components/consent/CookieBanner";
import { ConsentPreferences } from "@/components/consent/ConsentPreferences";
import {
  DEFAULT_CONSENT,
  loadConsent,
  saveConsent,
  clearConsent,
} from "@/lib/consent";

const ConsentContext = createContext(null);

/**
 * Global consent provider. Owns state, persists it, and renders the
 * banner + preferences dialog. Any component can call `useConsent()`.
 */
export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);   // null → not chosen yet
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  // Boot: load stored consent, otherwise open banner after a tick.
  useEffect(() => {
    const stored = loadConsent();
    if (stored) {
      setConsent(stored);
    } else {
      const t = setTimeout(() => setBannerOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = useCallback((partial) => {
    const value = saveConsent(partial);
    setConsent(value);
    setBannerOpen(false);
    setPrefsOpen(false);
  }, []);

  const acceptAll = useCallback(
    () => accept({ statistics: true, marketing: true }),
    [accept]
  );

  const rejectAll = useCallback(
    () => accept({ statistics: false, marketing: false }),
    [accept]
  );

  const revoke = useCallback(() => {
    clearConsent();
    setConsent(null);
    setBannerOpen(true);
  }, []);

  const openPreferences = useCallback(() => {
    setBannerOpen(false);
    setPrefsOpen(true);
  }, []);

  const value = {
    consent: consent || DEFAULT_CONSENT,
    hasChosen: consent !== null,
    acceptAll,
    rejectAll,
    accept,
    revoke,
    openPreferences,
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
      <CookieBanner
        open={bannerOpen && consent === null}
        onAcceptAll={acceptAll}
        onRejectAll={rejectAll}
        onPreferences={openPreferences}
      />
      <ConsentPreferences
        open={prefsOpen}
        initial={consent || DEFAULT_CONSENT}
        onClose={() => setPrefsOpen(false)}
        onSave={accept}
      />
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used inside ConsentProvider");
  return ctx;
}
